import { queryString } from './strings';
import { MultipartUrl, ParamsUrl } from './types';
import { checkTokenValidity } from './checkTokenValidity';

export interface FetchWrapper {
  url: string;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'MULTIPART';
  body?: any;
}

export interface FetchResponse<T> {
  status: number;
  statusText: string;
  ok: boolean;
  type: string;
  url: string;
  data?: T;
  errors?: Array<{ message: string }>;
}

const fetchWrapper = async <T>({
  url,
  method,
  body,
}: FetchWrapper): Promise<FetchResponse<T>> => {
  const token = await checkTokenValidity();

  const headers = new Headers();
  if (method !== 'MULTIPART') {
    headers.set('content-type', 'application/json;charset=UTF-8');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const options: RequestInit = {
    method: method === 'MULTIPART' ? 'POST' : method,
    headers,
  };

  if (body) {
    options.body = method === 'MULTIPART' ? body : JSON.stringify(body);
  }

  try {
    const response = await fetch(url, options);
    const responseJson = await response.json();

    return {
      status: responseJson?.data?.status || response.status,
      statusText: response.statusText,
      ok: response.ok,
      type: response.type,
      url: response.url,
      data: responseJson,
    };
  } catch (error) {
    return await Promise.reject(error);
  }
};

const fetchCall = async <T>({
  url,
  params,
}: ParamsUrl): Promise<FetchResponse<T>> => {
  const query = decodeURI(queryString(params));
  try {
    return await fetchWrapper({
      url: `${url}${query || ''}`,
      method: 'GET',
    });
  } catch (error: any) {
    return error;
  }
};

const postCall = async <T>({
  url,
  params,
}: ParamsUrl): Promise<FetchResponse<T>> => {
  try {
    return await fetchWrapper({ url, body: params, method: 'POST' });
  } catch (error: any) {
    return error;
  }
};

const patchCall = async <T>({
  url,
  params,
}: ParamsUrl): Promise<FetchResponse<T>> => {
  try {
    return await fetchWrapper({
      url,
      body: params,
      method: 'PATCH',
    });
  } catch (error: any) {
    return error;
  }
};

const deleteCall = async <T>({ url }: ParamsUrl): Promise<FetchResponse<T>> => {
  try {
    return await fetchWrapper({ url, method: 'DELETE' });
  } catch (error: any) {
    return error;
  }
};

const multipartCall = async <T>({
  url,
  formData,
}: MultipartUrl): Promise<FetchResponse<T>> => {
  try {
    return await fetchWrapper({ url, body: formData, method: 'MULTIPART' });
  } catch (error: any) {
    return error;
  }
};

export class apiClient {
  static get = fetchCall;
  static post = postCall;
  static patch = patchCall;
  static delete = deleteCall;
  static multipart = multipartCall;
}
